import { Box } from "@mui/material";
import { isValid } from "date-fns";
import { useCallback, useContext, useState } from "react";
import { NO_DRAG_SELECTOR } from "../../../app/constants";
import {
  formatDateRange,
  formatDateTime,
} from "../../../common/helpers/formatDateTime";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { ShiftOrderContext } from "../newOrder/short/OrderShortDataProvider";
import { OrderFields, OrderRow } from "../orderTypes";
import Buttons from "./Buttons";
import Header from "./Header";
import OrderAdditionalInfoForm from "./OrderAdditionalInfoForm";
import Preview from "./Preview";
import { ContentContainer } from "./styles";
import { FormValuesChangeValue, ShiftDetailsType } from "./types";

type Props = {
  row: OrderRow;
  rowId: string;
  values: OrderFields;
  bundleRowId: string | undefined;
};

const OrderFormShortAdditionalInfoContent = ({
  row,
  values,
  bundleRowId,
  rowId,
}: Props) => {
  const [formValues, setFormValues] = useState(values);
  const [periodStartDateIsRequired, setPeriodStartDateIsRequired] = useState<
    boolean | null
  >(null);

  const [shiftDetails, setShiftDetails] = useState<ShiftDetailsType[]>(
    row.rows.map((row) => ({
      date: row.date,
      time: row.time,
      customerCostCenter: row.customerCostCenter,
      id: row.id,
      isFillShift: row.isFillShift,
    })),
  );

  const {
    handleAdditionalInfoChange,
    handleToggleAdditionalInfo,
    handleDeleteRow,
    setTesVariation,
    validationErrors,
    handleError,
  } = useContext(ShiftOrderContext);

  const dateRange = row.rows.length > 1;
  const dates = dateRange
    ? (shiftDetails
        .map((values) => isValid(values.date) && values.date)
        .filter(Boolean) as Array<Date>)
    : null;

  const headerDateTime = dateRange
    ? formatDateRange(dates)
    : formatDateTime(values.date, values.time, "dateTimeRange");

  const handleFormValuesChange = useCallback(
    (name: string, value: FormValuesChangeValue) => {
      setFormValues((prevState) => ({ ...prevState, [name]: value }));
    },
    [],
  );

  const handleShiftDetailsChange = (
    name: string,
    value: Date | null | string | AutocompleteType | boolean,
    id: string,
  ) => {
    setShiftDetails(
      shiftDetails.map((details) =>
        details.id === id ? { ...details, [name]: value } : details,
      ),
    );
  };

  const onSave = () => {
    handleAdditionalInfoChange(rowId, formValues, shiftDetails, bundleRowId);
    handleToggleAdditionalInfo();
  };

  const hasValidationErrors =
    validationErrors &&
    !!validationErrors.find((error) => error.id === formValues.id);

  const periodStartDateMissing =
    !!periodStartDateIsRequired && !formValues?.orderTemplate?.periodStartDate;

  const saveDisabled = periodStartDateMissing || hasValidationErrors;

  return (
    <>
      <Box sx={{ height: "85vh" }}>
        <Header
          workTitle={values?.orderTemplate.name}
          workLocation={values?.workLocation.name}
          headerDateTime={headerDateTime}
        />
        <ContentContainer className={NO_DRAG_SELECTOR}>
          <OrderAdditionalInfoForm
            row={row}
            rowId={rowId}
            formValues={formValues}
            handleFormValuesChange={handleFormValuesChange}
            validationErrors={validationErrors}
            setTesVariation={setTesVariation}
            isNewOrder={true}
            shiftDetails={shiftDetails}
            handleShiftDetailsChange={handleShiftDetailsChange}
            handleError={handleError}
            setPeriodStartDateIsRequired={setPeriodStartDateIsRequired}
            periodStartDateIsRequired={periodStartDateIsRequired}
          />
          <Preview
            values={formValues}
            shiftDetails={shiftDetails}
            dates={dates}
          />
        </ContentContainer>
        <Buttons
          onSave={onSave}
          rowId={rowId}
          bundleRowId={bundleRowId}
          handleToggleAdditionalInfo={handleToggleAdditionalInfo}
          handleDeleteRow={handleDeleteRow}
          disabled={saveDisabled}
        />
      </Box>
    </>
  );
};

export default OrderFormShortAdditionalInfoContent;
